<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import { ref, computed } from 'vue'

// Basic app state
const drawer = ref(false)
const isDark = ref(false)
const route = useRoute()

// Show navigation for specific routes (inverted logic)
const shouldShowNavigation = computed(() => {
  const routeName = route.name

  // Show navigation for these routes (only error pages now)
  const navigationRoutes = [
    'not-found',
    'error',
    '404'
  ]

  // Only show navigation for error pages, hide for dynamic forms
  return navigationRoutes.includes(routeName as string) &&
    !route.meta?.fullscreen
})

// Navigation items (empty since we only show error pages)
const navigationItems: Array<{ title: string; icon: string; to: string }> = []

// Toggle theme
const toggleTheme = () => {
  isDark.value = !isDark.value
}
</script>

<template>
  <v-app :theme="isDark ? 'dark' : 'light'" :class="{ 'app-fullscreen': !shouldShowNavigation }">
    <!-- Main Content -->
    <v-main :class="{ 'fullscreen-main': !shouldShowNavigation }">
      <RouterView />
    </v-main>
  </v-app>
</template>

<style scoped>
/* Global background gradient */
.v-app {
  background: linear-gradient(180deg, #fff7d3 0%, #ffffff 100%);
  min-height: 100vh;
}

/* Default hidden state for navigation - no flash */
.app-navigation {
  transition: opacity 0.2s ease-in-out;
}

/* Full-screen mode styles */
.app-fullscreen .app-navigation {
  display: none !important;
}

.app-fullscreen .fullscreen-main {
  padding: 0 !important;
  margin: 0 !important;
}

/* Ensure proper full-screen layout */
.fullscreen-main {
  min-height: 100vh;
  width: 100%;
}

/* Override Vuetify margins in full-screen mode */
.app-fullscreen .v-main {
  padding-left: 0 !important;
  padding-right: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.app-fullscreen .v-main>.v-main__wrap {
  padding: 0 !important;
}

/* Ensure navigation elements are completely hidden by default until explicitly shown */
.v-app-bar,
.v-navigation-drawer,
.v-footer {
  visibility: hidden;
}

.app-navigation:is(.v-app-bar, .v-navigation-drawer, .v-footer) {
  visibility: visible;
}
</style>
